import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useState, type RefCallback, type RefObject } from "react";

import type { CartItemWithActions } from "../model/use-cart-model";

import { CartItem } from "./cart-item";
import { CartListSkeleton } from "./cart-item-skeleton";

type CartListProps = {
  items: CartItemWithActions[];
  isItemPending: (slug: string) => boolean;
  onProductClick?: () => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  cursorRef?: RefCallback<HTMLDivElement>;
  isFetchingNextPage?: boolean;
  savedScrollOffset?: number;
};

export function CartList({
  items,
  isItemPending,
  onProductClick,
  scrollContainerRef,
  cursorRef,
  isFetchingNextPage,
  savedScrollOffset = 0,
}: CartListProps) {
  const [isReady, setIsReady] = useState(false);

  // Wait for scroll container to be ready after drawer animation
  useEffect(() => {
    if (scrollContainerRef.current) {
      // Small delay to ensure drawer animation completed
      const timer = requestAnimationFrame(() => {
        setIsReady(true);
      });
      return () => cancelAnimationFrame(timer);
    }
  }, [scrollContainerRef]);

  // Restore scroll position after mounting
  useEffect(() => {
    if (isReady && savedScrollOffset > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollOffset;
    }
  }, [isReady, savedScrollOffset, scrollContainerRef]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => (isReady ? scrollContainerRef.current : null),
    estimateSize: () => 140,
    overscan: 5,
    initialOffset: savedScrollOffset,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Calculate total height based on items
  const totalHeight = items.length * 140;

  // Use virtual items if available, otherwise create fallback items
  const itemsToRender =
    virtualItems.length > 0
      ? virtualItems
      : items.map((_, i) => ({ index: i, start: i * 140, size: 140 }));

  return (
    <>
      <div
        style={{
          height: virtualizer.getTotalSize() || totalHeight,
          width: "100%",
          position: "relative",
        }}
      >
        {itemsToRender.map((row) => {
          const item = items[row.index];
          return (
            <div
              key={item.id}
              ref={virtualizer.measureElement}
              data-index={row.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${row.start}px)`,
              }}
            >
              <CartItem
                item={item}
                isPending={isItemPending(item.product.id)}
                onProductClick={onProductClick}
              />
            </div>
          );
        })}
      </div>
      {/* Cursor for infinity scroll */}
      {cursorRef && <div ref={cursorRef} className="h-2" />}
      {/* Loading indicator for next page */}
      {isFetchingNextPage && <CartListSkeleton count={2} />}
    </>
  );
}

// Re-export for convenience
export type { CartItemWithActions };
